import type { TenantUsageSummaryResponse } from '@contract-analyzer/api-client';
import { ArrowRightIcon } from 'lucide-react';
import { NavLink } from 'react-router';

import { canAccessWorkspaceBilling, canSubmitChargeCodes } from '@/components/charge-codes/charge-code-utils';
import { PIP_DISPLAY_LIMIT, QuotaRunway } from '@/components/quota-runway';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { useExtractionRunway } from '@/hooks/use-extraction-runway';
import { useNow } from '@/hooks/use-now';
import { useTenant } from '@/hooks/use-tenant';
import { billingSettingsPath } from '@/lib/routes';
import { formatTrialDate, type TrialPhase } from '@/lib/trial-phase';

interface QuotaBlockDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly resourceName: string;
  readonly now?: Date;
}

interface ResourceFacts {
  readonly quota: number;
  readonly used: number;
}

function resolveResourceFacts(
  resourceName: string,
  usage: TenantUsageSummaryResponse,
): ResourceFacts | null {
  if (resourceName === 'documents' && usage.document_quota > 0) {
    return { quota: usage.document_quota, used: usage.documents_uploaded };
  }
  if (resourceName === 'analysis runs' && usage.analysis_run_quota > 0) {
    return { quota: usage.analysis_run_quota, used: usage.analysis_runs_started };
  }
  return null;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-GB').format(value);
}

function dialogTitle(
  resourceName: string,
  facts: ResourceFacts | null,
  trial: TrialPhase,
  now: Date,
): string {
  // A trial that just ended is the reason the allowance applies at all; lead
  // with that so the number below is explained rather than surprising.
  if (trial.kind === 'ended') {
    return `Your trial ended on ${formatTrialDate(trial.endedAt, now)}`;
  }
  return facts !== null
    ? `You've used your ${formatCount(facts.quota)} free ${resourceName}`
    : `You've used the free ${resourceName} allowance`;
}

/**
 * The 402 moment, framed as a completed trial rather than a failure: what
 * the user made stays available, and the way forward is one action wide.
 * Charge-code-aware — a pending request reassures instead of selling.
 */
export function QuotaBlockDialog({
  open,
  onOpenChange,
  resourceName,
  now: fixedNow,
}: QuotaBlockDialogProps) {
  const now = useNow(fixedNow);
  const tenant = useTenant();
  const { user } = useAuth();
  const state = useExtractionRunway({ enabled: open });
  const facts = state.status === 'ready' ? resolveResourceFacts(resourceName, state.usage) : null;
  const chargeCodeReadiness = state.status === 'ready' ? state.chargeCodeReadiness : 'none';
  const trial: TrialPhase = state.status === 'ready' ? state.trial : { kind: 'none' };
  const billingPath = billingSettingsPath(tenant.slug);
  const canSubmitChargeCode = canSubmitChargeCodes(user);
  const canAccessBilling = canAccessWorkspaceBilling(user);

  const actionLabel =
    chargeCodeReadiness === 'pending'
      ? 'View request'
      : !canSubmitChargeCode
        ? 'View billing'
        : chargeCodeReadiness === 'rejected'
          ? 'Submit a different code'
          : 'Add charge code';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-bold tracking-tight">
            {dialogTitle(resourceName, facts, trial, now)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {trial.kind === 'ended' && facts !== null
              ? `The free allowance of ${formatCount(facts.quota)} ${resourceName} now applies and is used up. `
              : null}
            Nothing is lost — every result and export stays available, and new work resumes
            as soon as billing is set up.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {facts !== null && facts.quota <= PIP_DISPLAY_LIMIT ? (
          <QuotaRunway noun={`free ${resourceName}`} quota={facts.quota} used={facts.used} />
        ) : null}

        {chargeCodeReadiness === 'pending' ? (
          <div className="rounded-lg border bg-muted/40 p-4 text-sm font-light">
            <span className="font-medium">Charge code pending approval.</span> Work resumes
            automatically once it clears — nothing else to do.
          </div>
        ) : null}

        {!canAccessBilling ? (
          <p className="text-sm text-muted-foreground">Ask a workspace admin to set up billing.</p>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel className="h-11 px-4">View my results</AlertDialogCancel>
          {canAccessBilling ? (
            <Button asChild onClick={() => { onOpenChange(false); }} className="h-11">
              <NavLink to={billingPath}>
                {actionLabel}
                <ArrowRightIcon data-icon="inline-end" />
              </NavLink>
            </Button>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
